import { BikeConfig } from './config';
import { InputFrame } from './input';
import { clamp, Vec3, vec } from './math';

export interface RiderEffect {
  comOffsetZ: number;
  dampingScale: number;
}

/** Instant effect of an input (phase-1 API, kept for callers that have no rider state). */
export function riderEffect(input: InputFrame, cfg: BikeConfig): RiderEffect {
  return {
    comOffsetZ: clamp(input.body, -1, 1) * cfg.riderComShift,
    dampingScale: input.standing ? cfg.standingDampingScale : 1,
  };
}

/**
 * Where the rider actually is. The body axis (-1 back .. +1 forward) and the stance (0 seated .. 1 standing) move
 * at a finite rate toward what is wanted, like a person shifting weight; the foot comes out and goes back the same
 * way. On top of that the rider is a sprung mass (dy, dz: metres off his posture's place, up and forward in the
 * bike frame): through the seat when sitting (stiff), through knees and ankles on the pegs when standing (soft, so
 * the body soaks what the chassis does). The view reads all of it for the pose; the physics reads the COM.
 */
export interface RiderState {
  body: number;
  stand: number;
  foot: number;          // 0 on the peg .. 1 out
  footSide: -1 | 0 | 1;  // +1 left foot (turning left), -1 right, 0 none
  dy: number; dz: number; // m, sprung deviation from the posture's place (bike frame up / forward)
  vy: number; vz: number; // m/s of it
  autoStand: boolean;    // the posture controller's own choice (before a held Space flips it)
  holdFor: number;       // s the controller has wanted the other posture (hysteresis)
}

export const createRider = (): RiderState => ({ body: 0, stand: 0, foot: 0, footSide: 0, dy: 0, dz: 0, vy: 0, vz: 0, autoStand: false, holdFor: 0 });

const approach = (v: number, target: number, rate: number, dt: number) =>
  Math.abs(target - v) <= rate * dt ? target : v + Math.sign(target - v) * rate * dt;

/** Beginner assist: the inner foot goes out by itself in a slow turn. */
export function autoFootWanted(input: InputFrame, speed: number, roll: number, cfg: BikeConfig): boolean {
  return !!input.autoFoot && Math.abs(speed) < cfg.footMaxSpeed
    && (Math.abs(input.steer) > cfg.autoFootSteer || Math.abs(roll) > cfg.autoFootLean);
}

/**
 * What the posture controller reads (filled by the bike step, reused). All of it is what a rider sees and feels:
 * lean, the line he asked for, speed, the throttle and where it is going, the ground ahead.
 */
export interface PostureCues {
  speed: number;         // m/s
  roll: number;          // rad, + leaned left
  curvature: number;     // 1/m of the line the rider asks for
  throttle: number;
  throttleTrend: number; // 1/s, filtered rate of the throttle (+ rolling on)
  rough: number;         // m, the biggest bump (second difference of the ground) in the next few metres
  face: number;          // rise (m per m) of the ground over the next few metres: a jump face or a step
  airborne: boolean;
}
export const newPostureCues = (): PostureCues => ({ speed: 0, roll: 0, curvature: 0, throttle: 0, throttleTrend: 0, rough: 0, face: 0, airborne: false });

/** The posture controller's tuning (shared by every bike: it is the rider). */
export const POSTURE = {
  sitLean: 0.2,       // rad of lean that is a real corner: sit (owner 2026-10-06: seated through normal corners too)
  sitCurvature: 0.05, // 1/m (a 20 m radius) asked at speed: a real corner, sit
  standLean: 0.16,    // rad under which the bike is upright enough to stand again
  standSpeed: 5,      // m/s above which an upright bike on a straight stands
  slowSit: 2.5,       // m/s under which the rider sits (walking pace, feet near the ground)
  rough: 0.035,       // m of bump ahead that has him up on the pegs
  face: 0.22,         // rise per metre ahead that is a face or a step: stand for it
  exitTrend: 0.5,     // 1/s of throttle roll-on that, coming out of the corner, stands him up
  hold: 0.15,         // s the other posture must be wanted before he changes (hysteresis)
  footLean: 0.22,     // rad: leg out in a seated corner past this lean ...
  footCurvature: 0.09, // ... or this asked curvature
};

/** The automatic posture: true = stand. A pure decision with hysteresis on r.holdFor. */
export function postureWanted(r: RiderState, c: PostureCues, dt: number): boolean {
  const lean = Math.abs(c.roll), k = Math.abs(c.curvature), v = Math.abs(c.speed);
  const corner = !c.airborne && v > POSTURE.slowSit && (lean > POSTURE.sitLean || k > POSTURE.sitCurvature);
  const ahead = c.rough > POSTURE.rough || c.face > POSTURE.face;
  const straight = v > POSTURE.standSpeed && lean < POSTURE.standLean && k < POSTURE.sitCurvature * 0.6;
  const exit = v > POSTURE.slowSit && lean < POSTURE.sitLean && c.throttle > 0.4 && c.throttleTrend > POSTURE.exitTrend;
  let want: boolean;
  if (c.airborne || (ahead && v > POSTURE.slowSit)) want = true;       // jumps, landings, bumps: on the pegs
  else if (v < POSTURE.slowSit) want = false;                           // walking pace: seated
  else if (corner) want = false;                                        // a real corner: seated
  else if (exit || straight) want = true;                               // driving out of it / on the straight
  else want = r.autoStand;
  r.holdFor = want !== r.autoStand ? r.holdFor + dt : 0;
  // Into the air or onto a face the change is immediate; otherwise it waits out the hysteresis.
  if (want !== r.autoStand && (r.holdFor >= POSTURE.hold || c.airborne || (want && ahead))) { r.autoStand = want; r.holdFor = 0; }
  return r.autoStand;
}

/**
 * roll is + when leaned left (the lean controller's sign). feetDown: both feet on the ground (paddling).
 * cues: with input.autoPosture the rider picks sit / stand himself (a held Space flips his choice: input.standing).
 */
export function stepRider(r: RiderState, input: InputFrame, speed: number, roll: number, cfg: BikeConfig, dt: number, feetDown = false,
  cues: PostureCues | null = null): void {
  r.body = approach(r.body, clamp(input.body, -1, 1), cfg.bodyRate, dt);
  const standing = input.autoPosture && cues ? postureWanted(r, cues, dt) !== input.standing : input.standing;
  r.stand = approach(r.stand, standing ? 1 : 0, cfg.standRate, dt);
  // The inside leg: out only when seated, and in an auto-posture ride only in a real corner (row 2).
  const seated = r.stand < 0.5;
  const cornerLeg = !!input.autoPosture && cues !== null && seated && Math.abs(cues.speed) > 1
    && (Math.abs(roll) > POSTURE.footLean || Math.abs(cues.curvature) > POSTURE.footCurvature);
  const wanted = feetDown || (seated && (!!input.foot || autoFootWanted(input, speed, roll, cfg) || cornerLeg));
  r.foot = approach(r.foot, wanted ? 1 : 0, 6, dt);
  if (wanted) {
    const side = Math.abs(roll) > 0.03 ? Math.sign(roll) : Math.sign(cues && input.autoPosture ? cues.curvature : input.steer);
    if (side !== 0) r.footSide = side as 1 | -1;
    else if (r.footSide === 0) r.footSide = 1;
  } else if (r.foot === 0) r.footSide = 0;
}

/** Rider + bike COM relative to the bike centre, in the bike frame (x left, y up, z forward), at the posture's place. */
export const comOffset = (r: RiderState, cfg: BikeConfig): Vec3 =>
  vec(0, cfg.seatedComHeight + (cfg.standingComHeight - cfg.seatedComHeight) * r.stand,
    r.body * cfg.riderComShift * (1 + cfg.standBodyReach * r.stand * (r.body > 0 ? 1 : 0)));

/** Legs soak bumps when standing. */
export const legDamping = (r: RiderState, cfg: BikeConfig): number => 1 + (cfg.standingDampingScale - 1) * r.stand;
